import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { CaretLeft, DotsThreeVertical, Lightbulb, MagnifyingGlass, Timer, Trophy } from '@phosphor-icons/react';
import { Link, useNavigate } from '@tanstack/react-router';
import { css } from 'styled-system/css';
import type { Position, Puzzle } from '@/domain';
import type { PuzzleSolver, RevealedWordCell } from '@/application';
import type { ReportSurface, SurveyClient } from '@/application/survey';
import type { SoloEntriesStore } from '@/application/solo/SoloEntriesStore';
import type { SoundPlayer } from '@/application/session/SoundPlayer';
import type { SoundStore } from '@/application/session/SoundStore';
import type { SkipFilledStore } from '@/application/session/SkipFilledStore';
import { Button, ClueRail, Lockup } from '@/design-system';
import { t } from '@/ui/i18n';
import { AppShell } from '@/ui/v2/AppShell';
import { DesktopAppBar } from '@/ui/v2/DesktopAppBar';
import { MenuSheet } from '@/ui/v2/MenuSheet';
import { useGridNavigation, type Direction } from '@/ui/components/grid/useGridNavigation';
import { orderClues } from '@/ui/components/grid/orderClues';
import { BOARD_BOTTOM_GAP, posKey } from '@/ui/components/grid/playLayout';
import { PuzzleBoard, type PuzzleBoardHandle } from '@/ui/components/grid/PuzzleBoard';
import { useAdvanceOnValidation, inputAt } from '@/ui/components/grid/useAdvanceOnValidation';
import { ReportClueSheet } from '@/ui/components/grid/ReportClueSheet';
import { Keyboard } from './Keyboard';
import { useTouchPrimary, useResumeBlurOnPwa } from '@/ui/components/keyboard';
import { usePuzzleValidation } from '@/ui/components/grid/usePuzzleValidation';
import { useHintRequest } from '@/ui/components/grid/useHintRequest';
import { useGridVerification } from '@/ui/components/grid/useGridVerification';
import { ACTIVE_ASSIST_MODE } from '@/ui/components/grid/assistMode';
import { AssistCooldown, formatMmSs } from '@/ui/components/grid/AssistCooldown';
import { useAssistGate } from '@/ui/components/auth';
import { InfoPopover } from '@/ui/components/primitives/InfoPopover';
import { WinScreen } from './WinScreen';
import { useGridSounds } from './useGridSounds';
import { GridSoundToggle } from './GridSoundToggle';
import { formatClock } from '@/ui/lib/formatClock';
import { useIsDesktop } from '@/ui/lib/useIsDesktop';

const GUTTER = '14px';
// Overlay region: the grid bleeds behind it; pan-inset keeps the top reachable.
const header = css({ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 3, padding: `12px ${GUTTER} 0`, lg: { position: 'static', width: '100%', paddingTop: '24px', paddingInline: '36px' } });
// Frosted pill holds exit · brand · timer · settings, legible over the bleeding grid.
const headerBar = css({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  bg: 'ws.glass',
  backdropFilter: 'blur(10px)',
  border: '0.5px solid token(colors.ws.glassBorder)',
  borderRadius: '999px',
  padding: '5px 8px',
  boxShadow: '0 2px 12px rgba(33,75,64,0.14)',
  // Desktop: a plain transparent bar (matching the home top bar), not a frosted floating pill.
  lg: { bg: 'transparent', backdropFilter: 'none', border: 'none', borderRadius: 0, boxShadow: 'none', padding: '0', gap: '14px' },
});
const iconBtn = css({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  color: 'ws.jadeInk',
  fontSize: '18px',
  cursor: 'pointer',
  flex: 'none',
  _active: { background: 'rgba(33,75,64,0.08)' },
  _focusVisible: { outline: '3px solid token(colors.ws.sakuraRose)', outlineOffset: '2px' },
  // Desktop: frosted circle button matching the home top bar's menu button.
  lg: { width: '44px', height: '44px', background: 'ws.glass', boxShadow: '0 1px 2px rgba(33,75,64,0.08)', fontSize: '20px', _hover: { background: 'ws.glassHover' } },
});
const headerSpacer = css({ flex: 1 });
const brandLink = css({ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', borderRadius: '12px', _focusVisible: { outline: '3px solid token(colors.ws.sakuraRose)', outlineOffset: '4px' } });
const headerTimer = css({ display: 'inline-flex', alignItems: 'center', gap: '4px', fontFamily: 'wsMono', fontWeight: 'semibold', fontSize: '13.5px', color: 'ws.jadeInk', flex: 'none', fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em', paddingInline: '2px' });
const headerTimerIcon = css({ fontSize: '14px', opacity: 0.55, flex: 'none' });
// Mobile bleeds full-field behind floating bars; desktop is a flex-flow row under the app bar (the AppShell frame already reserves the bar), with the clue rail as a static sibling below.
const viewportFill = css({ flex: '1', minHeight: 0, lg: { width: '100%' } });

// Overlay bar (padBottom drives focus-reveal); mobile keeps no top-pad so the board bleeds under it. Desktop flows as the bottom row with an 8px gutter above the clue rail.
const bottomBar = css({ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 3, display: 'flex', flexDirection: 'column', gap: '10px', padding: `0 ${GUTTER} 14px`, md: { alignItems: 'center', '& > *': { width: '100%', maxWidth: '520px' } }, lg: { position: 'static', paddingTop: '8px', paddingBottom: '24px' } });
// Compact hint chip, lives in the ClueRail label row (replacing the counter).
const hintBtn = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  padding: '3px 9px',
  borderRadius: '999px',
  border: 'none',
  fontFamily: 'wsUi',
  fontWeight: 'bold',
  fontSize: '12px',
  color: 'ws.jadeInk',
  bg: '#F2EDDC',
  _dark: { bg: '#2A362E' },
  cursor: 'pointer',
  '&[aria-disabled=true]': { opacity: 0.45, cursor: 'not-allowed' },
  _focusVisible: { outline: '3px solid token(colors.ws.sakuraRose)', outlineOffset: '2px' },
});
const hintBulb = css({ color: 'ws.or' });
// Keeps the cooldown ring snug against the hint chip in the ClueRail trailing slot.
const hintTrailing = css({ display: 'inline-flex', alignItems: 'center', gap: '7px' });
// Inline hint error (e.g. 401 → "Connecte-toi…"); compact pill style to match the hint chip.
const hintError = css({
  alignSelf: 'center',
  padding: '4px 11px',
  borderRadius: '999px',
  fontFamily: 'wsUi',
  fontWeight: 'semibold',
  fontSize: '12px',
  color: 'ws.sakuraDark',
  bg: 'ws.hintErrorBg',
  _dark: { color: 'ws.sakuraRose' },
});
// Transient, non-cell-specific binary verdict (ADR-0076): no cell highlight, just a polite "not yet" line.
const failPill = css({
  alignSelf: 'center',
  textAlign: 'center',
  padding: '6px 14px',
  borderRadius: '999px',
  fontFamily: 'wsUi',
  fontWeight: 'semibold',
  fontSize: '13px',
  color: 'ws.jadeInk',
  bg: 'rgba(255,255,255,0.78)',
  _dark: { bg: 'rgba(23,41,33,0.82)' },
  border: '0.5px solid token(colors.ws.glassBorder)',
  boxShadow: '0 2px 12px rgba(33,75,64,0.12)',
});
// Post-win: bottom bar becomes a single re-entry to the celebration.
const resultsBtn = css({ width: '100%', gap: '9px' });


export interface PlayScreenProps {
  readonly puzzle: Puzzle;
  readonly puzzleSolver: PuzzleSolver;
  readonly soloEntriesStore: SoloEntriesStore;
  readonly soundPlayer?: SoundPlayer;
  readonly soundStore?: SoundStore;
  readonly skipFilledStore?: SkipFilledStore;
  readonly surveyClient?: SurveyClient;
  readonly reportSurface?: ReportSurface;
}

export function PlayScreen({ puzzle, puzzleSolver, soloEntriesStore, soundPlayer, soundStore, skipFilledStore, surveyClient, reportSurface = 'daily' }: PlayScreenProps) {
  // Resume from the persisted elapsed time (synced across devices via the progress blob) instead of restarting at 0.
  const [seconds, setSeconds] = useState(() => soloEntriesStore.loadElapsed(puzzle.id));
  const [winDismissed, setWinDismissed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const boardRef = useRef<PuzzleBoardHandle>(null);
  const touchPrimary = useTouchPrimary();
  // Pre-emptive blur on hide so reopening the PWA doesn't re-pop the OS keyboard (inputMode="none" is ignored on resume).
  useResumeBlurOnPwa(touchPrimary);
  // Measured height of the overlay bottom bar — PanZoom reserves it so the focused cell stays above.
  const bottomRef = useRef<HTMLDivElement>(null);
  const [bottomInset, setBottomInset] = useState(280);
  useEffect(() => {
    const el = bottomRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setBottomInset(el.offsetHeight));
    setBottomInset(el.offsetHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const isDesktop = useIsDesktop();

  // Persisted letters: seed the uncontrolled inputs and the auto-validation rehydration.
  const initialEntries = useMemo(() => soloEntriesStore.load(puzzle.id), [soloEntriesStore, puzzle.id]);
  const entryAt = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of initialEntries) m.set(posKey(e.row, e.column), e.letter);
    return m;
  }, [initialEntries]);

  // Locked cells (assist-revealed/verified + auto-validated words) seed synchronously from the store so validated cells never paint unvalidated first.
  const loadLocked = useCallback(
    (id: string) => new Set(soloEntriesStore.loadLockedCells(id).map((c) => posKey(c.row, c.column))),
    [soloEntriesStore],
  );
  const [lockedAssistCells, setLockedAssistCells] = useState<ReadonlySet<string>>(() => loadLocked(puzzle.id));
  const [lockedLoaded, setLockedLoaded] = useState(true);
  useEffect(() => {
    setLockedAssistCells(loadLocked(puzzle.id));
    setLockedLoaded(true);
  }, [puzzle.id, loadLocked]);

  // Gate the flatten ripple: only cells validated after a real interaction animate.
  const userActedRef = useRef(false);

  // On a solved verdict, lock every cell so Accueil's "Grille du jour" reads it as done and it syncs across devices (ADR-0075).
  const handleSolved = useCallback(
    (positions: ReadonlyArray<Position>) => {
      for (const p of positions) soloEntriesStore.lockCell(puzzle.id, p.row, p.col);
    },
    [soloEntriesStore, puzzle.id],
  );

  const validation = usePuzzleValidation(puzzle, puzzleSolver, handleSolved);
  const checkGrid = validation.onGridChanged;

  const handleCellChange = useCallback(
    (row: number, col: number, letter: string | null) => {
      userActedRef.current = true;
      soloEntriesStore.save(puzzle.id, row, col, letter);
      checkGrid();
    },
    [soloEntriesStore, puzzle.id, checkGrid],
  );

  const handleHintReveal = useCallback(
    (cells: ReadonlyArray<RevealedWordCell>) => {
      for (const cell of cells) {
        const input = document.querySelector<HTMLInputElement>(
          `input[data-cell-kind="letter"][data-row="${cell.row}"][data-col="${cell.column}"]`,
        );
        if (input) input.value = cell.letter;
        soloEntriesStore.save(puzzle.id, cell.row, cell.column, cell.letter);
        soloEntriesStore.lockCell(puzzle.id, cell.row, cell.column);
      }
      setLockedAssistCells((prev) => {
        const next = new Set(prev);
        for (const cell of cells) next.add(posKey(cell.row, cell.column));
        return next;
      });
      userActedRef.current = true;
      // A hint that fills the last cell triggers the whole-grid binary check.
      checkGrid();
    },
    [soloEntriesStore, puzzle.id, checkGrid],
  );

  const handleHintConsumed = useCallback(() => soloEntriesStore.recordHintUsed(puzzle.id), [soloEntriesStore, puzzle.id]);

  const hint = useHintRequest(
    puzzle.id,
    puzzle.hintsRemaining,
    puzzleSolver,
    handleHintReveal,
    handleHintConsumed,
    puzzle.secondsUntilNextHint ?? null,
  );

  const validatedPositions = useMemo<ReadonlySet<string>>(() => {
    if (lockedAssistCells.size === 0) return validation.validated;
    if (validation.validated.size === 0) return lockedAssistCells;
    const merged = new Set<string>(validation.validated);
    for (const k of lockedAssistCells) merged.add(k);
    return merged;
  }, [validation.validated, lockedAssistCells]);

  // Verify's correct cells lock via the same persist path hint reveal uses; letters are already saved (typed by the player).
  const handleVerifyCorrect = useCallback(
    (positions: ReadonlyArray<{ row: number; column: number }>) => {
      for (const p of positions) soloEntriesStore.lockCell(puzzle.id, p.row, p.column);
      setLockedAssistCells((prev) => {
        const next = new Set(prev);
        for (const p of positions) next.add(posKey(p.row, p.column));
        return next;
      });
      userActedRef.current = true;
      checkGrid();
    },
    [soloEntriesStore, puzzle.id, checkGrid],
  );

  // Verify plays its own woven sweep; this one-shot ref keeps the generic word cue from doubling up on the lock it triggers.
  const suppressWordCueRef = useRef(false);
  const verification = useGridVerification(
    puzzle,
    puzzleSolver,
    validatedPositions,
    handleVerifyCorrect,
    soundPlayer,
    suppressWordCueRef,
  );
  const assistGate = useAssistGate();

  // Stable ref so callbacks always read the latest validated set.
  const validatedRef = useRef(validatedPositions);
  validatedRef.current = validatedPositions;

  // Last focused cell + its axis — survive blur so the out-of-grid hint button can resolve the active word.
  const activeFocusRef = useRef<Position | null>(null);
  const activeDirectionRef = useRef<Direction>('across');

  const nav = useGridNavigation(puzzle, {
    onCellChange: handleCellChange,
    onFocusChange: (position, direction) => {
      if (!position) return;
      boardRef.current?.cancelBeat(); // a user tap during the beat skips it (no-op otherwise)
      activeFocusRef.current = position;
      if (direction) activeDirectionRef.current = direction;
    },
    // Suppress the hook's keyboard-avoidance auto-scroll — the grid never moves the view on its own.
    getZoomScale: () => 2,
    isCellValidated: (row, col) => validatedRef.current.has(posKey(row, col)),
    // Freeze input while the whole-grid verdict is in flight so a late keystroke can't overwrite the last letter or void a winning validation (ADR-0076).
    isInputLocked: () => validation.pending,
    skipFilledOnAdvance: () => skipFilledStore?.load() ?? true,
  });

  const { applyRemoteCellUpdate } = nav;
  // Solo resume: seed the live per-cell mirror from persisted entries, mirroring LiveCoopScreen's rejoin resync — otherwise a resumed cell's keycap tier reads `empty` until the player retypes it.
  useEffect(() => {
    for (const e of initialEntries) applyRemoteCellUpdate(e.row, e.column, e.letter);
  }, [initialEntries, applyRemoteCellUpdate]);

  const requestHint = useCallback(() => {
    const f = activeFocusRef.current;
    // Reveal re-checks the grid (ADR-0076 §7); mid-verdict that would bump requestSeqRef and drop it.
    if (!f || validatedRef.current.has(posKey(f.row, f.col)) || validation.pending) return;
    userActedRef.current = true;
    hint.request(f.row, f.col, activeDirectionRef.current);
  }, [hint, validation.pending]);

  const requestVerify = useCallback(() => {
    // Same race as requestHint: a correct-cell lock re-checks the grid mid-verdict.
    if (validation.pending) return;
    userActedRef.current = true;
    verification.verify();
  }, [verification, validation.pending]);

  const letterCount = useMemo(() => puzzle.cells.filter((c) => c.kind === 'letter').length, [puzzle]);
  const won = letterCount > 0 && validatedPositions.size >= letterCount;

  // Shared focus-advance firewall: after a word validates, move the cursor to the next word.
  const advance = useAdvanceOnValidation({ puzzle, nav, validatedPositions, currentClue: nav.currentClue, completed: won });

  useGridSounds({ validatedCount: validatedPositions.size, won, userActedRef, soundPlayer, suppressWordCueRef });

  // Clues ordered across-then-down; drives the ClueRail counter and the focus firewall.
  const orderedClues = useMemo(() => orderClues(puzzle), [puzzle]);

  // Definition cells whose every clue word is fully solved → lit "done" surface.
  const solvedDefCells = useMemo(() => {
    const wordsByDef = new Map<string, Position[][]>();
    for (const c of orderedClues) {
      const [dr, dc] = c.key.split(':');
      const defKey = `${dr},${dc}`;
      const arr = wordsByDef.get(defKey);
      if (arr) arr.push(c.cells);
      else wordsByDef.set(defKey, [c.cells]);
    }
    const done = new Set<string>();
    for (const [defKey, words] of wordsByDef) {
      if (words.every((w) => w.every((p) => validatedPositions.has(posKey(p.row, p.col))))) done.add(defKey);
    }
    return done;
  }, [orderedClues, validatedPositions]);

  const clue = nav.currentClue;
  const clueOrdinal = useMemo(() => {
    if (!clue) return -1;
    const k = `${clue.definition.position.row}:${clue.definition.position.col}:${clue.clue.arrow}`;
    return orderedClues.findIndex((c) => c.key === k);
  }, [clue, orderedClues]);

  // Rail stays mounted: shows live clue on focus, last clue on blur, first unfilled on load.
  const [lastOrdinal, setLastOrdinal] = useState(() => {
    const i = orderedClues.findIndex((c) => c.cells.some((p) => !entryAt.has(posKey(p.row, p.col))));
    return i < 0 ? 0 : i;
  });
  useEffect(() => {
    if (clueOrdinal >= 0) setLastOrdinal(clueOrdinal);
  }, [clueOrdinal]);
  const displayOrdinal = clueOrdinal >= 0 ? clueOrdinal : lastOrdinal;
  const displayClue = orderedClues[displayOrdinal];

  // Step clues: focused delegates to cycleClue; unfocused continues from the shown clue.
  const stepClue = useCallback(
    (dir: 1 | -1) => {
      boardRef.current?.cancelBeat(); // stepping the rail during the beat skips it
      advance.markJump(dir);
      if (nav.localCursor) {
        nav.cycleClue(dir);
        return;
      }
      const n = orderedClues.length;
      if (n === 0) return;
      const target = orderedClues[((displayOrdinal + dir) % n + n) % n];
      const cell = target.cells.find((p) => !validatedRef.current.has(posKey(p.row, p.col))) ?? target.cells[0];
      inputAt(cell.row, cell.col)?.focus();
    },
    [nav, orderedClues, displayOrdinal, advance],
  );

  // step-back is baked into eraseLetter; no focus nudge needed here.
  const playBackspace = useCallback(() => {
    nav.eraseLetter();
  }, [nav]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        e.preventDefault();
        playBackspace();
        return;
      }
      if (e.key === 'Tab') {
        advance.markJump(e.shiftKey ? -1 : 1);
      }
      nav.handleKeyDown(e);
    },
    [nav, playBackspace, advance],
  );

  // userActedRef gates out the mount-time validation of persisted entries, so reviewing a done grid never celebrates.
  const [wonLive, setWonLive] = useState(false);
  useEffect(() => {
    if (won && userActedRef.current) setWonLive(true);
  }, [won]);

  // Desktop only: touch devices pop the native keyboard on programmatic focus despite inputMode="none".
  const didAutoFocusRef = useRef(false);
  useEffect(() => {
    if (didAutoFocusRef.current || won || !lockedLoaded || touchPrimary) return;
    const cl = orderedClues.find((c) => c.cells.some((p) => !validatedPositions.has(posKey(p.row, p.col))));
    const target = cl?.cells.find((p) => !validatedPositions.has(posKey(p.row, p.col)));
    if (!cl || !target) return;
    didAutoFocusRef.current = true;
    inputAt(target.row, target.col)?.focus();
    // Toggle direction when the auto-focused clue is vertical (hook starts at 'across').
    if (!cl.across) nav.toggleDirection();
  }, [orderedClues, validatedPositions, won, lockedLoaded, nav, touchPrimary]);

  const handleReplay = useCallback(() => {
    soloEntriesStore.clearForPuzzle(puzzle.id);
    window.location.reload();
  }, [soloEntriesStore, puzzle.id]);

  const timeLabel = formatClock(seconds);

  useEffect(() => {
    if (won) return;
    const id = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [won]);

  // Latest tick, so the coalesced writers read it without re-subscribing every second.
  const secondsRef = useRef(seconds);
  secondsRef.current = seconds;
  // Coalesce localStorage writes: every 5s while running, plus on hide/unmount so a reload resumes the time.
  useEffect(() => {
    const persist = () => soloEntriesStore.saveElapsed(puzzle.id, secondsRef.current);
    const onHide = () => {
      if (document.visibilityState === 'hidden') persist();
    };
    const id = won ? undefined : window.setInterval(persist, 5000);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      if (id !== undefined) window.clearInterval(id);
      document.removeEventListener('visibilitychange', onHide);
      persist();
    };
  }, [soloEntriesStore, puzzle.id, won]);

  return (
    <AppShell
      variant="overlay"
      desktopBar={
        isDesktop ? (
          <DesktopAppBar
            trailing={
              <>
                <span className={headerTimer} aria-label={t('play.aria.timer', { time: timeLabel })}>
                  <Timer aria-hidden="true" weight="bold" className={headerTimerIcon} />
                  {timeLabel}
                </span>
                {soundStore ? <GridSoundToggle soundStore={soundStore} className={iconBtn} /> : null}
              </>
            }
          />
        ) : null
      }
      topBar={
        isDesktop ? undefined : (
          <header className={header}>
            <div className={headerBar}>
              <button type="button" className={iconBtn} onClick={() => navigate({ to: '/' })} aria-label={t('play.aria.exit')}>
                <CaretLeft aria-hidden="true" weight="bold" />
              </button>
              <Link to="/" className={brandLink} aria-label={t('play.aria.home')}>
                <Lockup orientation="horizontal" tone="jade" iconSize={26} textSize={17} gap={8} />
              </Link>
              <span className={headerSpacer} />
              <span className={headerTimer} aria-label={t('play.aria.timer', { time: timeLabel })}>
                <Timer aria-hidden="true" weight="bold" className={headerTimerIcon} />
                {timeLabel}
              </span>
              {soundStore ? <GridSoundToggle soundStore={soundStore} className={iconBtn} /> : null}
              <button type="button" className={iconBtn} onClick={() => setMenuOpen(true)} aria-label={t('play.aria.settings')}>
                <DotsThreeVertical aria-hidden="true" weight="bold" />
              </button>
            </div>
          </header>
        )
      }
      bottomBar={
        <div className={bottomBar} ref={bottomRef}>
          {!won && ACTIVE_ASSIST_MODE === 'hint' && hint.errorMessage ? (
            <p className={hintError} role="alert">
              {hint.errorMessage}
            </p>
          ) : null}
          {!won && ACTIVE_ASSIST_MODE === 'verify' && verification.errorMessage ? (
            <p className={hintError} role="alert">
              {verification.errorMessage}
            </p>
          ) : null}
          {!won && validation.failMessage ? (
            <p className={failPill} role="status" aria-live="polite">
              {validation.failMessage}
            </p>
          ) : null}
          {displayClue ? (
            <ClueRail
              direction={displayClue.across ? 'horizontal' : 'vertical'}
              directionLabel={t(displayClue.across ? 'clueRail.direction.horizontal' : 'clueRail.direction.vertical')}
              clue={displayClue.text}
              letterCount={displayClue.cells.length}
              letterCountLabel={t('clueRail.letterCount', { count: displayClue.cells.length })}
              index={displayOrdinal + 1}
              total={orderedClues.length}
              groupLabel={t('clueRail.aria.group')}
              counterLabel={t('clueRail.aria.counter', { index: displayOrdinal + 1, total: orderedClues.length })}
              prevLabel={t('clueRail.aria.prev')}
              nextLabel={t('clueRail.aria.next')}
              zoomInLabel={t('clueRail.aria.zoomIn')}
              zoomOutLabel={t('clueRail.aria.zoomOut')}
              onPrev={() => stepClue(-1)}
              onNext={() => stepClue(1)}
              onZoomIn={() => boardRef.current?.panZoom?.zoomIn()}
              onZoomOut={() => boardRef.current?.panZoom?.zoomOut()}
              report={
                surveyClient ? (
                  <ReportClueSheet
                    surveyClient={surveyClient}
                    surface={reportSurface}
                    clueText={displayClue.text}
                    puzzleId={puzzle.id}
                  />
                ) : undefined
              }
              trailing={
                won ? undefined : ACTIVE_ASSIST_MODE === 'verify' ? (
                  <span className={hintTrailing}>
                    <InfoPopover
                      info={assistGate ? assistGate.title : t('play.verify.info')}
                      onActivate={requestVerify}
                      disabled={
                        assistGate != null ||
                        verification.pending ||
                        validation.pending ||
                        (verification.secondsUntilNextVerify ?? 0) > 0
                      }
                    >
                      <button type="button" className={hintBtn} data-tour="assist">
                        <MagnifyingGlass aria-hidden="true" weight="bold" className={hintBulb} />
                        {t('play.verify.label')}
                      </button>
                    </InfoPopover>
                    <AssistCooldown
                      visible={verification.secondsUntilNextVerify !== null}
                      secondsRemaining={verification.secondsUntilNextVerify}
                      intervalSeconds={1800}
                      label={t('grid.verify.cooldown.label', {
                        time: formatMmSs(verification.secondsUntilNextVerify ?? 0),
                      })}
                      availableAnnouncement={t('grid.verify.cooldown.available')}
                    />
                  </span>
                ) : ACTIVE_ASSIST_MODE === 'hint' ? (
                  <span className={hintTrailing}>
                    <InfoPopover
                      info={assistGate ? assistGate.title : t('play.hint.info')}
                      onActivate={requestHint}
                      disabled={
                        assistGate != null ||
                        hint.pending ||
                        validation.pending ||
                        (hint.exhausted && (hint.secondsUntilNextHint ?? 0) > 0)
                      }
                    >
                      <button
                        type="button"
                        className={hintBtn}
                        data-tour="assist"
                        aria-label={t('play.hint.aria.remaining', { remaining: hint.hintsRemaining })}
                      >
                        <Lightbulb aria-hidden="true" weight="fill" className={hintBulb} />
                        {t('play.hint.label', { remaining: hint.hintsRemaining })}
                      </button>
                    </InfoPopover>
                    <AssistCooldown
                      visible={hint.hintsRemaining < puzzle.hintsAllowed && hint.secondsUntilNextHint !== null}
                      secondsRemaining={hint.secondsUntilNextHint}
                      intervalSeconds={600}
                      label={`+1 dans ${formatMmSs(hint.secondsUntilNextHint ?? 0)}`}
                      availableAnnouncement="Un indice est de nouveau disponible."
                      progressAnnouncement={`Régénération d’un indice en cours, ${hint.hintsRemaining} sur ${puzzle.hintsAllowed}.`}
                    />
                  </span>
                ) : null
              }
            />
          ) : null}
          {won ? (
            <Button variant="secondary" className={resultsBtn} onClick={() => { setWonLive(true); setWinDismissed(false); }}>
              <Trophy aria-hidden="true" weight="fill" />
              {t('play.results.cta')}
            </Button>
          ) : (
            <Keyboard onLetter={(l) => nav.enterLetter(l)} onBackspace={playBackspace} />
          )}
        </div>
      }
    >
      <PuzzleBoard
        ref={boardRef}
        puzzle={puzzle}
        nav={nav}
        validatedPositions={validatedPositions}
        rejectingPositions={ACTIVE_ASSIST_MODE === 'verify' ? verification.shakingPositions : undefined}
        entryAt={entryAt}
        solvedDefCells={solvedDefCells}
        className={viewportFill}
        padTop={isDesktop ? 18 : 68}
        padBottom={isDesktop ? 12 : bottomInset + BOARD_BOTTOM_GAP}
        padX={isDesktop ? 24 : 14}
        edgeFade
        onKeyDown={handleKeyDown}
        celebrateGuard={() => userActedRef.current}
        onBeatComplete={advance.onBeatComplete}
      />

      {wonLive && !winDismissed ? (
        <WinScreen time={timeLabel} onReplay={handleReplay} onDismiss={() => setWinDismissed(true)} />
      ) : null}

      <MenuSheet open={menuOpen} onClose={() => setMenuOpen(false)} />
    </AppShell>
  );
}
